import { cn } from '@fortem-monorepo/js-utilities'
import { ICG2Guidelines, ICGG2Report } from '@fortem-monorepo/web-sdk'

import { formatGuidelineNumber } from '@/pages/funds/ic-guidelines-v2/components/stages/breaches-stage/format-guideline-number'

import { getGuidelineValueScale } from './guideline-value-scale'

export interface GuidelineRowSnippetProps {
  result: ICGG2Report.GuidelineResult
  valueUnit?: 'unit' | 'percent' | 'basis_points' | null
}

export const GuidelineRowSnippet: React.FC<GuidelineRowSnippetProps> = ({ result, valueUnit }) => {
  const breachedRows = result.rows?.filter((row) => {
    return row.result?.status === 'hard' || row.result?.status === 'soft'
  })
  const aggregateResult = result.aggregate?.result ?? null
  const firstBreach = aggregateResult?.breach ?? breachedRows?.[0]?.result?.breach ?? null

  if (result.guideline.aggregation_function) {
    if (aggregateResult?.value === null || aggregateResult?.value === undefined) return null

    const lowerThreshold = getLowerThreshold(result)
    const upperThreshold = getUpperThreshold(result)

    return (
      <div className={'flex items-center gap-2'}>
        <span className={'tracking-wide uppercase text-xs text-muted-foreground'}>
          {result.guideline.aggregation_function}
        </span>
        <ThresholdValue result={result} value={lowerThreshold} />
        <span className={'text-sm text-muted-foreground/60'}>/</span>
        <span
          className={cn('font-semibold font-mono', getStatusTextClassName(aggregateResult.status))}
        >
          {formatGuidelineNumber(
            aggregateResult.value * getGuidelineValueScale(result.guideline, result.rows[0]?.row ?? {}, valueUnit),
            result.guideline.threshold_type,
          )}
        </span>
        <span className={'text-sm text-muted-foreground/60'}>/</span>
        <ThresholdValue result={result} value={upperThreshold} />
      </div>
    )
  }

  if (!firstBreach) {
    return null
  }

  const numSoftBreaches = breachedRows.filter((row) => row.result?.status === 'soft').length
  const numHardBreaches = breachedRows.filter((row) => row.result?.status === 'hard').length

  return (
    <div className={'flex items-center gap-2'}>
      {numSoftBreaches > 0 && (
        <>
          <span className={cn('tracking-wide uppercase font-semibold text-amber-600 font-mono')}>
            {numSoftBreaches}
          </span>
          <span className={'text-sm text-muted-foreground/60'}>/</span>
        </>
      )}
      {numHardBreaches > 0 && (
        <>
          <span className={cn('tracking-wide uppercase font-semibold text-red-600 font-mono')}>
            {numHardBreaches}
          </span>
          <span className={'text-sm text-muted-foreground/60'}>/</span>
        </>
      )}
      <span className={'tracking-wide text-sm text-muted-foreground'}>
        <span className={'font-mono'}>{result.rows.length}</span> evaluations
      </span>
    </div>
  )
}

const statusTextClassNames: Partial<Record<ICG2Guidelines.GuidelineStatus, string>> = {
  hard: 'text-red-600',
  soft: 'text-amber-600',
  ok: 'text-green-600',
  failed: 'text-blue-600',
}

const getStatusTextClassName = (status?: ICG2Guidelines.GuidelineStatus | null) => {
  return status ? statusTextClassNames[status] : undefined
}

const getLowerThreshold = (result: ICGG2Report.GuidelineResult) => {
  const thresholds = result.guideline.thresholds
  return thresholds.soft_lower ?? thresholds.hard_lower
}

const getUpperThreshold = (result: ICGG2Report.GuidelineResult) => {
  const thresholds = result.guideline.thresholds
  return thresholds.soft_upper ?? thresholds.hard_upper
}

const ThresholdValue: React.FC<{
  result: ICGG2Report.GuidelineResult
  value: number | null | undefined
}> = ({ result, value }) => {
  return (
    <span className={'text-sm text-muted-foreground font-mono'}>
      {value === null || value === undefined
        ? '-'
        : formatGuidelineNumber(value, result.guideline.threshold_type)}
    </span>
  )
}
